import * as React from "react";

import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import {
    asString,
    loadedDefinedMapWithDefault,
    asBoolean,
    loadedDefinedMap,
} from "common/components/component-helpers";
import { Data } from "common/components/data";
import { LOADING, ActionSource, AppEnvironment, AppOther, asUI } from "common/components/types";
import { GlideFunctionalRenderer } from "common/components/uis";
import { evalSummary } from "common/components/portable-renderers";
import {
    ImageAspectRatio,
    Corners,
    TextPosition,
    OverlayTextAlignment,
    TilesPadding,
    ImageGravity,
} from "common/generator/components/image-types";
import { defined, isEmptyOrUndefinedish } from "common/Support";
import { TextSize, TextStyle } from "common/generator/components/text-types";
import TileItem from "../ui/smart/tile-item/tile-item";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";

const ZeroWidthSpaceCharacterNOTEMPTY = "​";

export const VerticalTilesItemMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            titleValue,
            subtitleValue,
            ,
            size,
            padding,
            fallbackInitials,
            onTap,
            onDropImage,
            allowWrapping,
            corners,
            textPosition,
            overlayTextAlignment,
            textSize,
            textStyle,
            gravity,
            itemID,
            topLeftOverlay,
            topRightOverlay,
            bottomLeftOverlay,
            bottomRightOverlay,
        ],
        [titleVP, subtitleVP, imageVP],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClick, autoRunInTabletMode } = useOnClickFromAction(onTap, ActionSource.GridItem, ctx, env);
    const { onClickWithCustomContext: onDropCB } = useOnClickFromAction(onDropImage, ActionSource.GridItem, ctx, env);

    // eslint-disable-next-line prefer-const
    let { title, subtitle, imageSource } = evalSummary(titleValue, subtitleValue, imageVP, force);
    let fallback: string | undefined;
    if (fallbackInitials === LOADING) {
        fallback = "";
    } else {
        fallback = definedMap(fallbackInitials, asString);
    }

    const handleDropImage =
        onDropCB !== undefined
            ? (img: string) => {
                  const { isBuilder } = env.appEnvironment;
                  if (!isBuilder) return;
                  dontAwait(onDropCB(Data.fromJSON<AppOther, AppEnvironment>(img), undefined));
              }
            : undefined;

    const sizeValue = loadedDefinedMapWithDefault(size, ImageAspectRatio.ThreeByOne, asString) as ImageAspectRatio;
    const allowWrappingValue = loadedDefinedMapWithDefault(allowWrapping, false, asBoolean);
    const cornersValue = loadedDefinedMapWithDefault(corners, Corners.Rounded, asString) as Corners;
    const gravityValue = loadedDefinedMapWithDefault(gravity, ImageGravity.Center, asString) as ImageGravity;
    let textPositionValue = loadedDefinedMapWithDefault(textPosition, TextPosition.Below, asString) as
        | TextPosition
        | undefined;
    let overlayTextAlignmentValue = loadedDefinedMapWithDefault(
        overlayTextAlignment,
        OverlayTextAlignment.Center,
        asString
    ) as OverlayTextAlignment | undefined;
    const textSizeValue = loadedDefinedMapWithDefault(textSize, TextSize.Medium, asString) as TextSize;
    const textStyleValue = loadedDefinedMapWithDefault(textStyle, TextStyle.Normal, asString) as TextStyle;
    const paddingValue = loadedDefinedMapWithDefault(padding, TilesPadding.Loose, asString) as TilesPadding;

    const isCircle = sizeValue === ImageAspectRatio.Circle;

    if (isCircle) {
        textPositionValue = undefined;
        overlayTextAlignmentValue = undefined;
    }

    if (allowWrappingValue === false && textPositionValue === TextPosition.Below) {
        if (subtitleVP !== undefined && isEmptyOrUndefinedish(subtitle)) {
            subtitle = ZeroWidthSpaceCharacterNOTEMPTY;
        }
        if (titleVP !== undefined && isEmptyOrUndefinedish(title)) {
            title = ZeroWidthSpaceCharacterNOTEMPTY;
        }
    }

    const itemIDVal = loadedDefinedMap(itemID, asString);

    if (env.needsPush && autoRunInTabletMode && onClick !== undefined) {
        dontAwait(onClick(false));
    }

    return (
        <TileItem
            appEnvironment={env.appEnvironment}
            title={title}
            subtitle={subtitle}
            imageSource={imageSource}
            size={sizeValue}
            fallbackInitials={fallback}
            onClick={onClick}
            columnWidth={defined(env.columnWidth)}
            onDropImage={handleDropImage}
            allowWrapping={allowWrappingValue}
            cornersStyle={cornersValue}
            textPosition={textPositionValue}
            overlayTextAlignment={overlayTextAlignmentValue}
            textSize={textSizeValue}
            textStyle={textStyleValue}
            padding={paddingValue}
            gravity={gravityValue}
            itemID={itemIDVal}
            topLeftOverlay={loadedDefinedMap(topLeftOverlay, o => asUI(o).render(ctx, env))}
            bottomLeftOverlay={loadedDefinedMap(bottomLeftOverlay, o => asUI(o).render(ctx, env))}
            bottomRightOverlay={loadedDefinedMap(bottomRightOverlay, o => asUI(o).render(ctx, env))}
            topRightOverlay={loadedDefinedMap(topRightOverlay, o => asUI(o).render(ctx, env))}
        />
    );
};
